import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Check, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Profile, ProfilePatchRequest } from "@tilt/pos-sdk/types";

import { DeviceDefaults } from "../components/profile/DeviceDefaults";
import { MfaManager } from "../components/profile/MfaManager";
import { PinManager } from "../components/profile/PinManager";
import { getPosClient } from "../lib/api";
import { usePageTitle } from "../lib/use-page-title";

/**
 * Self-service profile page. Edits go through PATCH /pos/v1/profile/me
 * which updates user_tbl + Cognito attributes in one shot. The form
 * validates phone-as-E.164 client-side; the backend re-validates and
 * the regex is the same on both sides (catch typos early without
 * waiting for a 400).
 *
 * MFA management lands in POS-1 once supervisors actually need it.
 * For POS-0 we surface the current mfa_enrolled state read-only.
 */

const E164 = /^\+[1-9][0-9]{6,14}$/;

export function ProfilePage() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  usePageTitle("profile");
  const profile = useQuery({
    queryKey: ["profile"],
    queryFn: () => getPosClient().getProfile(),
  });

  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState<string | null>(null);

  useEffect(() => {
    if (profile.data) {
      setFirst(profile.data.first_name ?? "");
      setLast(profile.data.last_name ?? "");
      setPhone(profile.data.phone ?? "");
    }
  }, [profile.data]);

  const patch = useMutation({
    mutationFn: (body: ProfilePatchRequest) => getPosClient().patchMe(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });

  if (!profile.data) return null;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPhoneError(null);
    if (phone && !E164.test(phone)) {
      setPhoneError(t("profile.phoneError"));
      return;
    }
    const original = profile.data as Profile;
    const draft: { first_name?: string | null; last_name?: string | null; phone?: string | null } =
      {};
    if (first.trim() !== (original.first_name ?? "")) {
      draft.first_name = first.trim() || null;
    }
    if (last.trim() !== (original.last_name ?? "")) {
      draft.last_name = last.trim() || null;
    }
    if (phone.trim() !== (original.phone ?? "")) {
      draft.phone = phone.trim() || null;
    }
    if (Object.keys(draft).length === 0) return;
    patch.mutate(draft satisfies ProfilePatchRequest);
  }

  return (
    <div className="max-w-3xl space-y-6 pb-12">
      <header>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted">
          {t("profile.kicker")}
        </p>
        <h1 className="mt-1 text-2xl font-bold tracking-tight text-strong sm:text-3xl xl:text-4xl">
          {t("profile.title")}
        </h1>
        <p className="mt-1 text-sm text-muted">{t("profile.subtitle")}</p>
      </header>

      {/* Identity panel - drops the hard 1px ring border for the
          shadow-elev-1 token + rounded-xl, matches every other
          surface in the polish wave. */}
      <form
        onSubmit={onSubmit}
        className="space-y-5 rounded-xl bg-surface p-5 shadow-elev-1 sm:p-6"
      >
        <ReadOnly
          label={t("profile.fields.email")}
          value={profile.data.email}
          hint={t("profile.emailHint")}
        />
        <Field
          label={t("profile.fields.firstName")}
          value={first}
          onChange={setFirst}
          maxLength={100}
        />
        <Field
          label={t("profile.fields.lastName")}
          value={last}
          onChange={setLast}
          maxLength={100}
        />
        <Field
          label={t("profile.fields.phone")}
          value={phone}
          onChange={setPhone}
          placeholder={t("profile.phonePlaceholder")}
          error={phoneError}
          inputMode="tel"
        />
        <div className="flex items-center gap-3 pt-1">
          <button
            type="submit"
            disabled={patch.isPending}
            className="inline-flex min-h-tap items-center gap-2 rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-elev-1 transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {patch.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {t("common.saveChanges")}
          </button>
          {patch.isSuccess && (
            <span
              className="inline-flex items-center gap-1.5 text-sm text-emerald-700 dark:text-emerald-400"
              role="status"
            >
              <Check className="h-4 w-4" aria-hidden="true" />
              {t("common.saved")}
            </span>
          )}
          {patch.isError && (
            <span
              className="inline-flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400"
              role="alert"
            >
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              {(patch.error as Error)?.message ?? t("profile.updateError")}
            </span>
          )}
        </div>
      </form>

      <PinManager profile={profile.data} />

      <DeviceDefaults activeMembership={profile.data.active_membership} />

      <section className="rounded-xl bg-surface p-5 shadow-elev-1 sm:p-6">
        <h2 className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
          {t("profile.mfa.title")}
        </h2>
        <MfaManager enrolled={profile.data.mfa_enrolled} required={profile.data.mfa_required} />
      </section>
    </div>
  );
}

interface FieldProps {
  readonly label: string;
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly maxLength?: number;
  readonly placeholder?: string;
  readonly error?: string | null;
  readonly inputMode?: "tel" | "text";
}

function Field({ label, value, onChange, maxLength, placeholder, error, inputMode }: FieldProps) {
  return (
    <label className="block">
      <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
        {label}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        {...(maxLength !== undefined ? { maxLength } : {})}
        {...(placeholder ? { placeholder } : {})}
        {...(inputMode ? { inputMode } : {})}
        className="mt-1 block min-h-tap w-full rounded-md bg-surface-muted px-3 text-sm text-strong ring-1 ring-inset ring-line transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
      />
      {error && <span className="mt-1 block text-xs text-red-600 dark:text-red-400">{error}</span>}
    </label>
  );
}

function ReadOnly({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div>
      <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-muted">
        {label}
      </span>
      <div className="mt-1 rounded-md bg-surface-muted px-3 py-2.5 font-mono text-sm text-foreground">
        {value}
      </div>
      {hint && <span className="mt-1 block text-xs text-muted">{hint}</span>}
    </div>
  );
}
